// JST 基準の日付ユーティリティ。プロジェクト規約により時刻は常に Asia/Tokyo 基準で扱う。
/**
 * `YYYY-MM-DD` を JST の曜日（DayOfWeek）に変換。
 *
 * `Intl.DateTimeFormat` の `timeZone: 'Asia/Tokyo'` 指定で、
 * Node プロセスの TZ や OS のロケールに依存せず JST の曜日を返す。
 */
export function dayOfWeekJST(date) {
    const d = new Date(`${date}T00:00:00+09:00`);
    const short = new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        timeZone: 'Asia/Tokyo',
    })
        .format(d)
        .toLowerCase();
    return short;
}
/**
 * 現在の JST 暦日を `YYYY-MM-DD` 形式で返す。
 *
 * `Intl.DateTimeFormat` の `timeZone: 'Asia/Tokyo'` で env TZ 非依存に
 * JST 日付を取得し、`en-CA` ロケール (`YYYY-MM-DD` 出力) を使って整形。
 *
 * `now` 引数は注入用（テストで時刻固定）。未指定時は `new Date()`。
 */
export function todayJst(now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
/**
 * 現在を JST 基準の Iso8601 文字列（`YYYY-MM-DDTHH:mm:ss+09:00`）で返す。
 *
 * `Intl.DateTimeFormat`（`timeZone: 'Asia/Tokyo'` / `hourCycle: 'h23'`）で env TZ 非依存に
 * JST の壁時計を取得し、固定オフセット `+09:00`（JST は通年 DST 無し）を付与する。
 * functions 側の `formatInTimeZone(new Date(), 'Asia/Tokyo', "yyyy-MM-dd'T'HH:mm:ssXXX")` と
 * 出力形式を揃え、フロント（date-fns 非依存）でも JST 規約準拠の Iso8601 を生成できる。
 *
 * `now` 引数は注入用（テストで時刻固定）。未指定時は `new Date()`。
 */
export function nowIsoJst(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}+09:00`;
}
/**
 * 現在の JST 時刻を `HH:mm` 形式で返す（24h 表記、ゼロパディング）。
 *
 * `todayJst` と対になるユーティリティ。`bookings/{id}` の `startTime`/`endTime`
 * (`TimeString` = `HH:mm`) と辞書式比較するため、フォーマットを揃える。
 *
 * `now` 引数は注入用（テストで時刻固定）。未指定時は `new Date()`。
 */
export function nowTimeJst(now = new Date()) {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Tokyo',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(now);
}
/**
 * JST の `YYYY-MM-DD` に N 日加算した結果を返す。
 *
 * 内部では `${date}T12:00:00+09:00` で正午相当の Date を作り、ms 加算後に
 * `todayJst` と同じ `Intl.DateTimeFormat` で再整形する。JST は通年 +09:00
 * 固定で DST が無いため、正午基準なら ±1 ミリ秒差の境界倒れも起きない。
 *
 * 入力形式が `YYYY-MM-DD` と異なれば throw。
 */
export function addDaysJst(date, days) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error(`Invalid date string: "${date}". Expected "YYYY-MM-DD".`);
    }
    const base = new Date(`${date}T12:00:00+09:00`);
    if (Number.isNaN(base.getTime())) {
        throw new Error(`Invalid date string: "${date}".`);
    }
    const shifted = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
    return todayJst(shifted);
}
/**
 * JST の `YYYY-MM-DD` に N か月加算した結果を返す（M-75 周期計算用 / 負数可）。
 *
 * 存在しない日は当月末日へ丸める（月末クランプ）: 2026-01-31 +1M → 2026-02-28。
 * `YYYY-MM-DD` は暦日のみでオフセット無関係のため、UTC メソッドで暦計算する
 * （TZ 非依存 / DST の影響なし）。入力形式が不正なら throw。
 */
export function addMonthsJst(date, months) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        throw new Error(`Invalid date string: "${date}". Expected "YYYY-MM-DD".`);
    }
    if (Number.isNaN(new Date(`${date}T12:00:00+09:00`).getTime())) {
        throw new Error(`Invalid date string: "${date}".`);
    }
    const [y, m, d] = date.split('-').map(Number);
    // 月初に寄せてから月を進め、日を月末クランプで適用する。
    const target = new Date(Date.UTC(y, m - 1 + months, 1));
    const daysInMonth = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, daysInMonth));
    const yy = target.getUTCFullYear();
    const mm = String(target.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(target.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
}
//# sourceMappingURL=dateJst.js.map